import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, Stack, Chip, Avatar,
  Collapse, useTheme, IconButton, Tooltip, Snackbar, Alert,
  TextField, CircularProgress, LinearProgress,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import EmailIcon from '@mui/icons-material/Email';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { api } from '../hooks/useApi';
import { useRealtimeTranscription } from '../hooks/useRealtimeTranscription';
import BillingSuggestions from './BillingSuggestions';
import RecordingModal from './RecordingModal';
import { glass } from '../themes';

interface Props {
  patientId: number;
  showNewEncounter?: boolean;
  onNewEncounterChange?: (show: boolean) => void;
}

export default function PatientDetail({ patientId, showNewEncounter = false, onNewEncounterChange }: Props) {
  const [patient, setPatient] = useState<any>(null);
  const [todayEncounter, setTodayEncounter] = useState<any>(null);
  const [pastEncounters, setPastEncounters] = useState<any[]>([]);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const theme = useTheme();

  const load = async () => {
    try {
      const [p, today, all] = await Promise.all([
        api.getPatient(patientId),
        api.getTodayEncounter(patientId),
        api.getEncounters(patientId),
      ]);
      setPatient(p);
      setTodayEncounter(today);
      const todayStr = new Date().toISOString().split('T')[0];
      setPastEncounters(all.filter((e: any) => e.date !== todayStr));
    } catch (err) {
      console.error('[PatientDetail] load error:', err);
      // Fallback: load just patient + encounters without today's auto-create
      try {
        const [p, all] = await Promise.all([api.getPatient(patientId), api.getEncounters(patientId)]);
        setPatient(p);
        setPastEncounters(all);
      } catch (err2) {
        console.error('[PatientDetail] fallback load error:', err2);
      }
    }
  };

  useEffect(() => { load(); }, [patientId]);
  if (!patient) return <Box sx={{ minHeight: '60vh' }} />;

  const handleNoteAdded = (note: any) => {
    setTodayEncounter((prev: any) => ({ ...prev, notes: [...(prev.notes || []), note] }));
  };

  const handleNoteDeleted = (noteId: number) => {
    setTodayEncounter((prev: any) => ({ ...prev, notes: (prev.notes || []).filter((n: any) => n.id !== noteId) }));
  };

  const handleSoapGenerated = (updated: any) => {
    setTodayEncounter((prev: any) => ({ ...prev, ...updated }));
  };

  const handleBillingUpdated = (codes: any[]) => {
    api.updateEncounter(todayEncounter.id, {
      date: todayEncounter.date,
      subjective: todayEncounter.subjective,
      objective: todayEncounter.objective,
      assessment: todayEncounter.assessment,
      plan: todayEncounter.plan,
      raw_notes: todayEncounter.raw_notes,
      billing_codes: codes,
    });
    setTodayEncounter((prev: any) => ({ ...prev, billing_codes: codes }));
  };

  return (
    <Box>
      {/* Patient header */}
      <Box sx={{ mb: 3, pb: 2.5, borderBottom: `1px solid rgba(255,255,255,0.6)` }}>
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Avatar sx={{
            width: 56, height: 56,
            bgcolor: '#1e3a5f', color: '#fff',
            fontWeight: 700, fontSize: '1.2rem',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          }}>
            {patient.first_name[0]}{patient.last_name[0]}
          </Avatar>
          <Box>
            <Typography sx={{ fontFamily: '"DM Serif Display", serif', fontSize: '1.4rem', lineHeight: 1.2 }}>
              {patient.first_name} {patient.last_name}
            </Typography>
            {patient.notes ? (
              <>
                <Typography color="text.secondary" sx={{ fontSize: '15px', fontWeight: 500, ...(notesExpanded ? { whiteSpace: 'pre-wrap' } : { display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}>
                  {patient.notes}
                </Typography>
                <Typography variant="caption" onClick={() => setNotesExpanded(!notesExpanded)}
                  sx={{ color: '#1e3a5f', cursor: 'pointer', display: 'block', mt: 0.25 }}>
                  {notesExpanded ? 'See less' : 'See more'}
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {pastEncounters.length + (todayEncounter ? 1 : 0)} encounter{(pastEncounters.length + (todayEncounter ? 1 : 0)) !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {patient.date_of_birth && (
            <Chip icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />} label={patient.date_of_birth} size="small"
              sx={{ bgcolor: theme.palette.accent.yellow, color: theme.palette.text.primary, fontWeight: 500, '& .MuiChip-icon': { color: theme.palette.text.primary } }} />
          )}
          {patient.phone && (
            <Chip icon={<LocalPhoneIcon sx={{ fontSize: 14 }} />} label={patient.phone} size="small"
              sx={{ bgcolor: theme.palette.accent.green, color: theme.palette.text.primary, fontWeight: 500, '& .MuiChip-icon': { color: theme.palette.text.primary } }} />
          )}
          {patient.email && (
            <Chip icon={<EmailIcon sx={{ fontSize: 14 }} />} label={patient.email} size="small"
              sx={{ bgcolor: theme.palette.accent.purple, color: theme.palette.text.primary, fontWeight: 500, '& .MuiChip-icon': { color: theme.palette.text.primary } }} />
          )}
        </Stack>
      </Box>

      {/* Today's encounter */}
      {todayEncounter && (
        <TodayEncounter
          encounter={todayEncounter}
          onNoteAdded={handleNoteAdded}
          onNoteDeleted={handleNoteDeleted}
          onSoapGenerated={handleSoapGenerated}
          onBillingUpdated={handleBillingUpdated}
        />
      )}

      {/* Past encounters */}
      {pastEncounters.length > 0 && (
        <Box mt={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Typography sx={{ fontFamily: '"DM Serif Display", serif', fontSize: '1.1rem', color: glass.text }}>
              Past Encounters
            </Typography>
            <Box
              onClick={async () => {
                const enc = await api.createEncounter({ patient_id: patientId });
                setPastEncounters(prev => [enc, ...prev]);
              }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1.5, py: 0.75, borderRadius: '12px',
                bgcolor: '#1e3a5f', color: '#fff',
                cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
                boxShadow: '0 2px 10px rgba(30,58,95,0.2)',
                '&:active': { opacity: 0.85 },
              }}
            >
              <AddIcon sx={{ fontSize: 16 }} /> New Encounter
            </Box>
          </Stack>
          <Stack spacing={1.5}>
            {pastEncounters.map(enc => (
              <EncounterCard key={enc.id} enc={enc} />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

/* ── Today's encounter ── */

function TodayEncounter({ encounter, onNoteAdded, onNoteDeleted, onSoapGenerated, onBillingUpdated }: {
  encounter: any;
  onNoteAdded: (note: any) => void;
  onNoteDeleted: (noteId: number) => void;
  onSoapGenerated: (updated: any) => void;
  onBillingUpdated: (codes: any[]) => void;
}) {
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [soapProgress, setSoapProgress] = useState(0);
  const [soapDone, setSoapDone] = useState(false);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showBilling, setShowBilling] = useState(false);
  const [recordingModalOpen, setRecordingModalOpen] = useState(false);
  const theme = useTheme();

  const noteTextRef = useRef('');
  const onCommit = useCallback((text: string) => {
    setNoteText(prev => {
      const next = prev ? prev + ' ' + text : text;
      noteTextRef.current = next;
      return next;
    });
  }, []);
  const { isRecording, partialText, startTranscription, stopTranscription } = useRealtimeTranscription({ onCommit });

  // Auto-save when recording stops
  const prevRecording = useRef(false);
  useEffect(() => {
    if (prevRecording.current && !isRecording) {
      // Give onCommit a tick to flush the last partial
      setTimeout(() => {
        const content = noteTextRef.current.trim();
        if (content) {
          api.addNote(encounter.id, content).then(note => {
            onNoteAdded(note);
            setNoteText('');
            noteTextRef.current = '';
          }).catch(console.error);
        }
      }, 100);
    }
    prevRecording.current = isRecording;
  }, [isRecording]);

  const hasSoap = encounter.subjective || encounter.objective || encounter.assessment || encounter.plan;
  const soapSections = [
    { key: 'subjective', short: 'S', label: 'Subjective', color: theme.palette.accent.green },
    { key: 'objective', short: 'O', label: 'Objective', color: theme.palette.accent.yellow },
    { key: 'assessment', short: 'A', label: 'Assessment', color: theme.palette.accent.purple },
    { key: 'plan', short: 'P', label: 'Plan', color: theme.palette.accent.coral },
  ] as const;

  const handleAddNote = async () => {
    const content = noteText.trim();
    if (!content) return;
    setSaving(true);
    try {
      const note = await api.addNote(encounter.id, content);
      onNoteAdded(note);
      setNoteText('');
    } finally {
      setSaving(false);
    }
  };

  const stopSoapTimers = () => {
    if (progressTimerRef.current) { clearInterval(progressTimerRef.current); progressTimerRef.current = null; }
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
  };

  const handleGenerateSoap = async () => {
    if (generating) return;
    try {
      // Save any unsaved text before generating
      const pending = noteText.trim();
      if (pending) {
        setNoteText('');
        noteTextRef.current = '';
        const note = await api.addNote(encounter.id, pending);
        onNoteAdded(note);
      }
      await api.generateSoap(encounter.id);
    } catch (err: any) {
      alert('Failed to generate SOAP: ' + err.message);
      return;
    }

    setGenerating(true);
    setSoapProgress(0);
    setSoapDone(false);
    stopSoapTimers();

    // Progress bar: asymptotically approaches 92% over ~10s
    progressTimerRef.current = setInterval(() => {
      setSoapProgress(p => p + (92 - p) * 0.12);
    }, 400);

    // Poll every 3s for completion
    pollTimerRef.current = setInterval(async () => {
      try {
        const updated = await api.getEncounter(encounter.id);
        if (updated.soap_status !== 'generating') {
          stopSoapTimers();
          setSoapProgress(100);
          setSoapDone(true);
          onSoapGenerated(updated);
          setTimeout(() => { setGenerating(false); setSoapDone(false); setSoapProgress(0); }, 1500);
        }
      } catch {}
    }, 3000);
  };

  const handleDeleteNote = async (noteId: number) => {
    await api.deleteNote(encounter.id, noteId);
    onNoteDeleted(noteId);
  };

  const displayText = partialText ? (noteText ? noteText + ' ' + partialText : partialText) : noteText;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Box>
          <Typography sx={{ fontFamily: '"DM Serif Display", serif', fontSize: '1.15rem', color: glass.text }}>
            Today's Encounter
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: glass.textSecondary }}>
            {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>
        </Stack>

      {/* Notes list */}
      {(encounter.notes?.length > 0) && (
        <Stack spacing={1} mb={1.5}>
          {encounter.notes.map((note: any) => (
            <Box key={note.id} sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1,
              bgcolor: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.7)',
              borderRadius: '12px', px: 1.5, py: 1, backdropFilter: 'blur(10px)',
            }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.7rem', color: glass.textSecondary, mb: 0.25 }}>
                  {new Date(note.created_at + 'Z').toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', color: glass.text, whiteSpace: 'pre-wrap' }}>
                  {note.content}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => handleDeleteNote(note.id)}
                sx={{ color: 'rgba(0,0,0,0.2)', mt: 0.25, '&:hover': { color: 'rgba(180,0,0,0.5)' } }}>
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      {/* Add note input */}
      <Box sx={{ position: 'relative', mb: 1.5 }}>
        <TextField
          multiline minRows={2} fullWidth
          placeholder="Add a note — labs, observations, updates…"
          value={displayText}
          onChange={e => { if (!isRecording) { setNoteText(e.target.value); noteTextRef.current = e.target.value; } }}
          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleAddNote(); }}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: {
              fontSize: '0.9rem', lineHeight: 1.6, color: glass.text,
              p: 1.5, pr: 6, bgcolor: 'rgba(255,255,255,0.5)',
              border: '1px solid rgba(255,255,255,0.7)',
              borderRadius: '12px', backdropFilter: 'blur(10px)',
            },
          }}
        />
        <IconButton
          onClick={() => { startTranscription(); setRecordingModalOpen(true); }}
          size="small"
          sx={{
            position: 'absolute', right: 8, bottom: 8,
            bgcolor: 'rgba(30,58,95,0.1)', color: '#1e3a5f',
            '&:hover': { bgcolor: 'rgba(30,58,95,0.15)' },
          }}
        >
          <MicIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Stack direction="row" justifyContent="flex-end" gap={1}>
        {/* Add Note */}
        <Box
          onClick={handleAddNote}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 2, py: 0.75, borderRadius: '12px',
            bgcolor: saving || !noteText.trim() ? 'rgba(0,0,0,0.05)' : 'rgba(30,58,95,0.08)',
            color: saving || !noteText.trim() ? glass.textSecondary : '#1e3a5f',
            cursor: saving || !noteText.trim() ? 'default' : 'pointer',
            fontWeight: 600, fontSize: '0.85rem',
            border: '1px solid rgba(30,58,95,0.12)',
            transition: 'all 0.15s',
          }}
        >
          {saving ? <CircularProgress size={14} /> : <AddIcon sx={{ fontSize: 16 }} />}
          Add Note
        </Box>

        {/* Generate SOAP */}
        {(encounter.notes?.length > 0 || noteText.trim()) && (
          <Box
            onClick={handleGenerateSoap}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1.5, py: 0.75, borderRadius: '12px',
              bgcolor: generating ? 'rgba(0,0,0,0.05)' : '#1e3a5f',
              color: generating ? glass.textSecondary : '#fff',
              cursor: generating ? 'default' : 'pointer',
              fontWeight: 600, fontSize: '0.85rem',
              border: '1px solid rgba(30,58,95,0.12)',
              boxShadow: generating ? 'none' : '0 2px 10px rgba(30,58,95,0.2)',
              transition: 'all 0.15s',
            }}
          >
            <AutoFixHighIcon sx={{ fontSize: 16 }} />
            Generate SOAP
          </Box>
        )}
      </Stack>

      {/* Progress bar shown while generating */}
      {generating && (
        <Box sx={{ mt: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography sx={{ fontSize: '0.75rem', color: glass.textSecondary }}>
              {soapDone ? 'SOAP note ready' : 'Generating SOAP note…'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: glass.textSecondary }}>
              {Math.round(soapProgress)}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={soapProgress}
            sx={{
              height: 5, borderRadius: 3,
              bgcolor: 'rgba(30,58,95,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                bgcolor: soapDone ? '#2e7d32' : '#1e3a5f',
                transition: soapDone ? 'all 0.4s ease' : 'value 0.4s linear, background-color 0.4s',
              },
            }}
          />
        </Box>
      )}

      {/* Generated SOAP */}
      {hasSoap && (
        <Box mt={2.5}>
          <Typography sx={{ fontFamily: '"DM Serif Display", serif', fontSize: '1rem', color: glass.text, mb: 1 }}>
            SOAP Note
          </Typography>
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.7)',
            borderRadius: '14px', overflow: 'hidden',
          }}>
            <Stack spacing={0}>
              {soapSections.map(({ key, short, label, color }, idx) => {
                const text = encounter[key];
                if (!text) return null;
                return (
                  <Box key={key} sx={{
                    display: 'flex', alignItems: 'stretch',
                    borderBottom: idx < 3 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                  }}>
                    <Box sx={{ width: 4, bgcolor: color, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, py: 1.25, px: 1.5 }}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(0,0,0,0.35)', mb: 0.4 }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{text}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {/* Billing */}
          {showBilling ? (
            <Box mt={1.5}>
              <BillingSuggestions codes={encounter.billing_codes} onAccept={codes => { onBillingUpdated(codes); setShowBilling(false); }} />
            </Box>
          ) : (
            <Stack direction="row" spacing={1} mt={1.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
              {encounter.billing_codes?.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={0.5} flex={1}>
                  {encounter.billing_codes.map((bc: any, i: number) => (
                    <Chip key={i} label={`${bc.code} — ${bc.description}`} size="small" sx={{
                      bgcolor: bc.type === 'CPT' ? theme.palette.accent.green : theme.palette.accent.coral,
                      color: theme.palette.text.primary, fontWeight: 600, fontSize: '0.7rem',
                    }} />
                  ))}
                </Stack>
              )}
              <Box
                onClick={() => setShowBilling(true)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  px: 1.5, py: 0.75, borderRadius: '12px', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem',
                  bgcolor: 'rgba(59,184,184,0.1)', color: '#2a8f8f',
                  border: '1px solid rgba(59,184,184,0.25)',
                  '&:hover': { bgcolor: 'rgba(59,184,184,0.18)' },
                }}
              >
                Suggest Billing
              </Box>
            </Stack>
          )}
        </Box>
      )}

      <RecordingModal
        open={recordingModalOpen}
        transcript={noteTextRef.current}
        partial={partialText}
        agentState={{ status: 'idle', quickResponse: '', fullResponse: '', error: '' }}
        isRecording={isRecording}
        onStop={() => { setRecordingModalOpen(false); stopTranscription(); }}
        onDismiss={() => setRecordingModalOpen(false)}
        onFollowUp={() => {}}
      />
    </Box>
  );
}

/* ── Past encounter card ── */

function EncounterCard({ enc }: { enc: any }) {
  const [open, setOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const theme = useTheme();
  const hasSoap = enc.subjective || enc.objective || enc.assessment || enc.plan;

  const soapSections = [
    { key: 'subjective', short: 'S', label: 'Subjective', color: theme.palette.accent.green },
    { key: 'objective', short: 'O', label: 'Objective', color: theme.palette.accent.yellow },
    { key: 'assessment', short: 'A', label: 'Assessment', color: theme.palette.accent.purple },
    { key: 'plan', short: 'P', label: 'Plan', color: theme.palette.accent.coral },
  ] as const;

  const copyToClipboard = async () => {
    let text = '';
    if (enc.date) {
      text += `Date: ${new Date(enc.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}\n\n`;
    }
    if (enc.raw_notes) text += `Notes:\n${enc.raw_notes}\n\n`;
    if (hasSoap) {
      text += 'SOAP Note:\n\n';
      soapSections.forEach(({ key, label }) => {
        if (enc[key]) text += `${label.toUpperCase()}:\n${enc[key]}\n\n`;
      });
    }
    if (enc.billing_codes?.length > 0) {
      text += 'Billing Codes:\n';
      enc.billing_codes.forEach((bc: any) => { text += `- ${bc.code}: ${bc.description}\n`; });
    }
    try { await navigator.clipboard.writeText(text.trim()); setCopySuccess(true); } catch (err) { console.error('[EncounterCard] clipboard error:', err); }
  };

  // Keyboard shortcut: Ctrl/Cmd+C to copy when expanded
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if Ctrl+C or Cmd+C
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        // Don't trigger if user is typing in an input/textarea/contentEditable
        // Check both the event target and the currently focused element
        const target = e.target as HTMLElement;
        const activeEl = document.activeElement as HTMLElement;
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable ||
          activeEl?.tagName === 'INPUT' ||
          activeEl?.tagName === 'TEXTAREA' ||
          activeEl?.isContentEditable
        ) {
          return;
        }
        e.preventDefault();
        copyToClipboard();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, enc]);

  const preview = enc.raw_notes?.split('\n')[0] || (hasSoap ? 'SOAP note' : '');

  return (
    <>
      <Card sx={{
        background: theme.palette.background.paper,
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        border: `1px solid rgba(255,255,255,0.6)`,
        boxShadow: '0 2px 16px rgba(0,0,0,0.04)', overflow: 'visible',
      }}>
        <Box onClick={() => setOpen(o => !o)}
          sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer', userSelect: 'none' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.text.secondary, display: 'block' }}>
              {enc.date ? new Date(enc.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : 'No date'}
            </Typography>
            {!open && preview && (
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(0,0,0,0.45)', mt: 0.25, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                {preview}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" ml={1}>
            {hasSoap && <Chip label="SOAP" size="small" sx={{ bgcolor: theme.palette.accent.purple, color: theme.palette.text.primary, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />}
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: 'rgba(0,0,0,0.35)', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
          </Stack>
        </Box>
        <Collapse in={open}>
          <Box sx={{ px: 2, pb: 2, borderTop: `1px solid rgba(255,255,255,0.6)`, pt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Tooltip title="Copy to clipboard">
                <IconButton onClick={e => { e.stopPropagation(); copyToClipboard(); }} size="small"
                  sx={{ bgcolor: theme.palette.accent.purple, color: theme.palette.text.primary, '&:hover': { bgcolor: theme.palette.accent.purpleDark } }}>
                  <ContentCopyIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            {enc.raw_notes && (
              <Box mb={hasSoap ? 2 : 0}>
                <Typography sx={{ whiteSpace: 'pre-wrap', color: theme.palette.text.primary, fontSize: '15px' }}>{enc.raw_notes}</Typography>
              </Box>
            )}
            {hasSoap && (
              <Stack spacing={1.5}>
                {soapSections.map(({ key, short, color }) => {
                  const text = enc[key];
                  if (!text) return null;
                  return (
                    <Box key={key}>
                      <Typography variant="caption" fontWeight={700} sx={{
                        display: 'inline-block', mb: 0.5, px: 1.5, py: 0.25,
                        borderRadius: 10, bgcolor: color, color: theme.palette.text.primary,
                        fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em',
                      }}>
                        {short}
                      </Typography>
                      <Typography sx={{ whiteSpace: 'pre-wrap', pl: 0.5, fontSize: '15px' }}>{text}</Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}
            {enc.billing_codes?.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={0.5} mt={2} pt={1.5} sx={{ borderTop: `1px solid rgba(255,255,255,0.6)` }}>
                {enc.billing_codes.map((bc: any, i: number) => (
                  <Chip key={i} label={`${bc.code} - ${bc.description}`} size="small" sx={{
                    bgcolor: bc.type === 'CPT' ? theme.palette.accent.green : theme.palette.accent.coral,
                    color: theme.palette.text.primary, fontWeight: 600, fontSize: '0.7rem',
                  }} />
                ))}
              </Stack>
            )}
          </Box>
        </Collapse>
      </Card>
      <Snackbar open={copySuccess} autoHideDuration={2000} onClose={() => setCopySuccess(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setCopySuccess(false)} severity="success" sx={{ width: '100%' }}>Copied to clipboard!</Alert>
      </Snackbar>
    </>
  );
}
